import {useDispatch, useSelector} from "react-redux";
import {Button, Spin, Typography, Card, Row, Col, Statistic, Empty, Pagination, Space, Tooltip, Popconfirm} from "antd";
import api from "../api";
import {useEffect, useState} from "react";
import {handleQuizStat} from "../functions";
import QuestionItem from "../Quiz/Fragments/QuestionItem";
import {Helmet} from "react-helmet-async";
import {useTranslation} from "react-i18next";
import {CloseCircleOutlined, RightOutlined, SwapOutlined, DeleteOutlined, QuestionCircleOutlined, CheckCircleOutlined} from '@ant-design/icons';
import ChatPromoBanner from "../Fragments/ChatPromoBanner";

const { Title, Paragraph } = Typography;

function Wrongs({analytics}){
    const [loading, setLoading] = useState(false);
    const [question, setQuestion] = useState(null);
    const { t } = useTranslation();
    const [answered, setAnswered] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const dispatch = useDispatch();
    const wrongs = useSelector((state) => state.questions || [])
    
    useEffect(() => {
        analytics()
    }, [])

    useEffect(() => {
        if (wrongs.length > 0) {
            // If current page is now out of bounds (e.g. after answering correctly)
            if (currentPage > wrongs.length) {
                setCurrentPage(wrongs.length || 1);
            } else {
                fetchQuestion(currentPage);
            }
        } else {
            setQuestion(null);
        }
    }, [wrongs.length, currentPage])

    const fetchQuestion = (page) => {
        setAnswered(false)
        setQuestion(null)
        const questionId = wrongs[page - 1];
        if (questionId) {
            setLoading(true)
            api.API.get(api.Question + `/${questionId}`).then(question => {
                setQuestion(question)
                setLoading(false)
            }).catch(() => {
                setLoading(false);
            })
        }
    }

    const handleNext = (variant) => {
        handleQuizStat(question.id, variant === question.answer)
        if (variant === question.answer) {
            dispatch({
                payload: question.id,
                type: 'QESTION_PASSED'
            })
        }
        setAnswered(variant)
    }

    const onPageChange = (page) => {
        setCurrentPage(page);
    }

    const handleNextQuestion = () => {
        if (currentPage <= wrongs.length) {
            // After answering a question correctly, it's removed, so we don't necessarily need to increment page
            // if we want to show the NEXT item in the list which now occupies the same index.
            // But if it was incorrect, we might want to move to next.
            if (answered === question.answer) {
                 // Question removed, current index now points to next item
                 if (currentPage > wrongs.length) {
                     setCurrentPage(1);
                 } else {
                     fetchQuestion(currentPage);
                 }
            } else {
                if (currentPage < wrongs.length) {
                    setCurrentPage(currentPage + 1);
                } else {
                    setCurrentPage(1);
                }
            }
        }
    }

    const handleShuffle = () => {
        if (wrongs.length > 1) {
            const randomIndex = Math.floor(Math.random() * wrongs.length) + 1;
            if (randomIndex === currentPage) {
                fetchQuestion(currentPage);
            } else {
                setCurrentPage(randomIndex);
            }
        }
    }

    const handleClearAll = () => {
        dispatch({ type: 'CLEAR_WRONGS' });
    }

    return (
        <div className="wrongs-container" style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
            <Helmet>
                <title>{t('seo.wrongs_title')}</title>
                <meta name="description" content={t('seo.wrongs_desc')} />
            </Helmet>
           {/* <ScriptAd scriptUrl={'https://pl28772667.effectivegatecpm.com/49/ee/ea/49eeeaf9b0d350a53916971b7e02f91e.js'}/>
            <ScriptAd scriptUrl={'https://flaskledgeheadquarters.com/c6/ee/1e/c6ee1ebe112c3166dd00a4273faba949.js'}/>*/}
            <div style={{ marginBottom: 30 }}>
                <Row gutter={[16, 16]} align="bottom">
                    <Col xs={24} sm={16}>
                        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <CloseCircleOutlined style={{ color: '#cf1322' }} />
                            {t('header.wrongs')}
                        </Title>
                        <Paragraph type="secondary" style={{ marginTop: 8, fontSize: 16 }}>
                            {t('wrongs.desc_1')}
                        </Paragraph>
                    </Col>
                    <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                        <Space size="large" direction="vertical" align="end">
                            <Statistic

                                value={wrongs.length}
                                valueStyle={{ color: '#cf1322', fontWeight: 'bold' }}
                            />
                            {wrongs.length > 0 && (
                                <Popconfirm 
                                    title={t('wrongs.clear_all_confirm')} 
                                    onConfirm={handleClearAll}
                                    okText={t('home.yes')}
                                    cancelText={t('home.no')}
                                >
                                    <Button type="link" danger icon={<DeleteOutlined />} style={{ padding: 0 }}>{t('wrongs.clear_all_btn')}</Button>
                                </Popconfirm>
                            )}
                        </Space>
                    </Col>
                </Row>
            </div>

            {wrongs.length > 0 && (
                <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
                    <Card size="small" style={{ borderRadius: 20, background: '#fafafa', border: '1px solid #f0f0f0' }}>
                        <Space size="middle">
                            <Tooltip title={t('wrongs.random_question')}>
                                <Button 
                                    icon={<SwapOutlined />} 
                                    onClick={handleShuffle}
                                    disabled={wrongs.length <= 1}
                                >
                                    {t('wrongs.shuffle')}
                                </Button>
                            </Tooltip>
                            <div style={{ width: 1, height: 20, background: '#ddd' }} />
                            <Pagination
                                simple
                                current={currentPage}
                                total={wrongs.length}
                                pageSize={1}
                                onChange={onPageChange}
                            />
                        </Space>
                    </Card>
                </div>
            )}

            <Spin spinning={loading} size="large" tip={t('wrongs.loading')}>
                {question ? (
                    <div className="animate-in">
                        <QuestionItem 
                            quick_view={true} 
                            currentQuestion={question} 
                            handleNext={handleNext} 
                            value={answered} 
                            noSubmit={answered !== false}
                        />
                        
                        <div style={{ 
                            marginTop: 30, 
                            padding: '20px',
                            background: '#fff',
                            borderRadius: 16,
                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                            textAlign: 'center'
                        }}>
                            <Row gutter={[16, 16]} justify="center" align="middle">
                                <Col xs={24} md={16}>
                                    <Pagination
                                        current={currentPage}
                                        total={wrongs.length}
                                        pageSize={1}
                                        onChange={onPageChange}
                                        showSizeChanger={false}
                                        showQuickJumper
                                        style={{ display: 'inline-block' }}
                                    />
                                </Col>
                                {answered !== false && (
                                    <Col xs={24} md={8}>
                                        <Button 
                                            size="large" 
                                            type="primary" 
                                            icon={<RightOutlined />} 
                                            onClick={handleNextQuestion}
                                            style={{ 
                                                minWidth: 150, 
                                                borderRadius: 10, 
                                                height: 45, 
                                                background: answered === question.answer ? '#52c41a' : '#1890ff',
                                                borderColor: answered === question.answer ? '#52c41a' : '#1890ff',
                                                boxShadow: `0 4px 10px ${answered === question.answer ? 'rgba(82, 196, 26, 0.3)' : 'rgba(24, 144, 255, 0.3)'}`
                                            }}
                                        >
                                            {t('questions.next')}
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    </div>
                ) : (
                    !loading && (
                        <Card style={{ textAlign: 'center', padding: '40px 0', borderRadius: 20 }}>
                            <Empty 
                                image={<CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />}
                                description={
                                    <div style={{ marginBottom: 20 }}>
                                        <Title level={4} style={{ color: '#52c41a' }}>{t('wrongs.congrats_no_wrongs')}</Title>
                                        <Paragraph type="secondary">
                                            {t('wrongs.no_wrongs_desc')}
                                        </Paragraph>
                                    </div>
                                }
                            >
                                <Button 
                                    type="primary" 
                                    size="large" 
                                    onClick={() => window.location.href = '/quizes'}
                                    style={{ borderRadius: 10, height: 50, minWidth: 200 }}
                                    icon={<QuestionCircleOutlined />}
                                >
                                    {t('questions.tests')}
                                </Button>
                            </Empty>
                        </Card>
                    )
                )}
            </Spin>
            
            <style jsx>{`
                .animate-in {
                    animation: fadeIn 0.5s ease-out;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .wrongs-container :global(.ant-pagination-simple-pager) {
                    font-weight: bold;
                }
            `}</style>
        </div>
    )
}

export default Wrongs;
